/**
 * Pre-order dates, UK style — DD-MM-YYYY instead of YYYY-MM-DD.
 *
 * The Notify Me app writes its dates in ISO format, e.g. "Expected availability:
 * 2026-11-25" in the selling plan box and "Finished at the factory from:
 * 2026-11-25" next to the pre-order button. That text is the app's own markup and
 * the theme has no setting for the format, so we rewrite it after it lands.
 *
 * The same date also reaches the cart as the "Expected availability" line item
 * property, which snippets/line-item.liquid prints as-is in the cart drawer, the
 * cart page and the customer's order page.
 *
 * Text arrives after page load in all of these: the app adds its own on every
 * variant change (when <product-rerender> swaps the markup) and inside quick-buy
 * drawers, and the cart drawer re-renders its line items whenever the cart
 * changes. A one-off pass is not enough, so a MutationObserver formats each piece
 * of text as it arrives.
 *
 * Only visible text is changed. The hidden `properties[Expected availability]`
 * input keeps the app's ISO value: it is an attribute, not a text node, so the
 * walker never sees it. That value is saved on the order and the app may read it
 * back, so it is left exactly as the app wrote it. For the same reason checkout
 * and order emails still show ISO dates: the theme cannot reach them.
 */
(function () {
  "use strict";

  // Text outside these is never touched:
  //   .product-info       product pages, featured product sections, quick-buy drawers
  //   .line-item__info    line items in the cart drawer, cart page and order page
  const SCOPE_SELECTOR = ".product-info, .line-item__info";

  // YYYY-MM-DD with a real month and day, not part of a longer number or code.
  // The leading character is captured rather than using a lookbehind, which older
  // Safari versions reject as a syntax error (taking the whole script down).
  const ISO_DATE = /(^|[^\d-])(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?![\d-])/g;

  const SKIPPED_PARENTS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"]);

  function formatTextNode(node) {
    const text = node.nodeValue;

    if (!text || !node.parentNode || SKIPPED_PARENTS.has(node.parentNode.nodeName)) {
      return;
    }

    const formatted = text.replace(ISO_DATE, "$1$4-$3-$2");

    // Only write when something changed. Writing fires the observer again, but
    // the second pass finds no ISO dates left, so it stops there.
    if (formatted !== text) {
      node.nodeValue = formatted;
    }
  }

  function formatWithin(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      formatTextNode(root);
      return;
    }

    if (root.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      formatTextNode(walker.currentNode);
    }
  }

  function isInScope(node) {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return Boolean(element && element.closest(SCOPE_SELECTOR));
  }

  // Covers both cases: a node added inside a product info column, and a whole
  // product info column added at once (a quick-buy drawer opening).
  function formatNode(node) {
    if (isInScope(node)) {
      formatWithin(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      node.querySelectorAll(SCOPE_SELECTOR).forEach(formatWithin);
    }
  }

  document.querySelectorAll(SCOPE_SELECTOR).forEach(formatWithin);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData") {
        if (isInScope(mutation.target)) {
          formatTextNode(mutation.target);
        }
        return;
      }

      mutation.addedNodes.forEach(formatNode);
    });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
})();
